import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import { MonthlyRegistration } from '../../models/dashboard.model';

export type MonthlyChartValueFormat = 'vendors' | 'currency';

@Component({
  selector: 'app-monthly-chart',
  standalone: true,
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './monthly-chart.component.html',
  styleUrl: './monthly-chart.component.scss',
})
export class MonthlyChartComponent implements OnInit {
  @Input() data: MonthlyRegistration[] = [];
  @Input() title = 'Monthly Registrations';
  @Input() valueFormat: MonthlyChartValueFormat = 'vendors';

  barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => this.formatTooltipValue(context?.parsed?.y),
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 12,
            family: 'Inter'
          }
        },
        border: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#9CA3AF',
          font: {
            size: 11,
            family: 'Inter'
          },
          padding: 8,
          callback: (value) => this.formatTickValue(value),
        },
        grid: {
          color: '#F3F4F6',
          // drawBorder: false,
          lineWidth: 1,
          // lineDash: [4, 4]
        },
        border: {
          display: false
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };

  barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: '#3B82A0',
      borderRadius: 6,
      borderSkipped: false,
      barPercentage: 0.6,
      categoryPercentage: 0.8
    }]
  };

  ngOnInit() {
    this.updateChartData();
  }

  ngOnChanges() {
    this.updateChartData();
  }

  private updateChartData() {
    if (this.data && this.data.length > 0) {
      this.updateYAxisScale(this.data.map((d) => Number(d.count ?? 0)));
      this.barChartData = {
        labels: this.data.map(d => d.month),
        datasets: [{
          data: this.data.map(d => d.count),
          backgroundColor: this.data.map((_, i) => {
            // Highlight the last bar (Jul) with a lighter shade
            return i === this.data.length - 1 ? '#5BA3C0' : '#3B82A0';
          }),
          borderRadius: 6,
          borderSkipped: false,
          barPercentage: 0.6,
          categoryPercentage: 0.8
        }]
      };
    }
  }

  private formatTickValue(value: unknown): string {
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) return '';

    if (this.valueFormat === 'currency') return this.formatCurrency(numericValue);
    return String(numericValue);
  }

  private formatTooltipValue(value: unknown): string {
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) return '';

    if (this.valueFormat === 'currency') return this.formatCurrency(numericValue);
    return `${numericValue} vendors`;
  }

  private formatCurrency(value: number): string {
    return `₦${Math.round(value).toLocaleString('en-NG')}`;
  }

  private updateYAxisScale(values: number[]): void {
    if (!this.barChartOptions?.scales) return;
    const options = this.barChartOptions as any;

    if (this.valueFormat === 'vendors') {
      options.scales = options.scales ?? {};
      options.scales['y'] = options.scales['y'] ?? {};
      options.scales['y'].max = 35;
      options.scales['y'].ticks = { ...(options.scales['y'].ticks ?? {}), stepSize: 8 };
      return;
    }

    const maxValue = Math.max(0, ...values.filter((v) => Number.isFinite(v)));
    const step = this.getNiceStep(maxValue / 4 || 1);
    const max = Math.ceil(maxValue / step) * step;

    options.scales = options.scales ?? {};
    options.scales['y'] = options.scales['y'] ?? {};
    options.scales['y'].max = max;
    options.scales['y'].ticks = { ...(options.scales['y'].ticks ?? {}), stepSize: step };
  }

  private getNiceStep(value: number): number {
    if (!Number.isFinite(value) || value <= 0) return 1;
    const exponent = Math.floor(Math.log10(value));
    const fraction = value / Math.pow(10, exponent);
    const niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
    return niceFraction * Math.pow(10, exponent);
  }
}
